// Client-side AI timing — ONE place, so the four chat surfaces, the phone, the
// draft/idea assistants and every background memory writer agree.
//
// WHY THESE NUMBERS (prompt shape A2, 2026-09-08): ai-proxy now runs every
// model with hidden reasoning ON at high effort. A story reply is projected at
// a 40-60 s median, and the SERVER holds a hard deadline of 120 s (100 s for
// extraction/assist features) after which it answers HTTP 504
// { code: 'deadline' }. Supabase edge functions themselves idle-close at 150 s.
//
//   AI_ABORT_MS  — the client's own fetch abort. It MUST sit above the server's
//                  120 s deadline plus network slack, or the client walks away
//                  from a reply the server is still (billably) writing and
//                  never sees the 504/502 it would have been told about.
//   AI_SAFETY_MS — typing indicators and reveal/hold locks: belt-and-braces
//                  timers that clear UI state if a promise is lost. They must
//                  outlive the abort and never fire mid-generation.
//
// Before this file the client aborted at 45-90 s and the safety timers fired at
// 60 s — every one of them would have cut a legitimate reasoning-on reply.
export const AI_ABORT_MS = 140000;
export const AI_SAFETY_MS = 150000;

// ai-proxy's two billed-in-full terminal failures. Both mean a whole
// generation was paid for and nothing usable came back. Neither is ever
// auto-retried (aiRetry.js peeks the body) and neither may trigger the
// near-duplicate regeneration path — the player presses resend on purpose.
//   504 { code: 'deadline',     elapsed_ms, budget_sent }
//   502 { code: 'budget_spent', reasoning_tokens, budget_sent, elapsed_ms }
export const AI_DEADLINE_CODE = 'deadline';
export const AI_BUDGET_SPENT_CODE = 'budget_spent';
export const AI_DEADLINE_MSG = 'The reply ran past the time limit — press resend.';
export const AI_BUDGET_SPENT_MSG = 'The model spent its whole budget thinking and no reply survived — press resend.';
// ai-proxy v116 adds `reason: 'host_cut'` when the PROVIDER ended the reply
// (finish_reason error / content_filter) rather than the envelope running out —
// the old copy blamed reasoning for a host fault (2026-10-19 audit: five of
// Haley's DM turns, zero reasoning tokens, 3-10 s).
export const AI_HOST_CUT_MSG = 'The host cut the reply short before it was finished — press resend.';

// A SUCCESSFUL reply may carry `truncated: true`: the visible text hit the
// budget and was trimmed to its last complete sentence. It is shown as-is;
// the surface adds this note next to its existing regenerate affordance.
export const AI_TRUNCATED_NOTE = 'cut at the budget — regenerate for the full reply';

export function isBilledFailureCode(code) {
  return code === AI_DEADLINE_CODE || code === AI_BUDGET_SPENT_CODE;
}

/**
 * Player-facing text for an ai-proxy error body. The two billed failures get
 * their fixed copy; anything else falls back to the server's `error` string,
 * then to `fallback`.
 * @param {{ code?: string, error?: string } | null | undefined} data
 * @param {string} [fallback]
 */
export function aiProxyErrorMessage(data, fallback = 'AI request failed') {
  if (data?.code === AI_DEADLINE_CODE) return AI_DEADLINE_MSG;
  if (data?.code === AI_BUDGET_SPENT_CODE) return data?.reason === 'host_cut' ? AI_HOST_CUT_MSG : AI_BUDGET_SPENT_MSG;
  return data?.error || fallback;
}
